'use strict';

// ─── Help dialog ──────────────────────────────────────────────────────────────

import { WORDLIST_PUBLISHERS, MERGED_NAME } from '../../core/constants.js';
import { esc, pluralize } from '../../core/util.js';
import { getBrowser } from '../../core/platform.js';
import { FEATURED_TOOLS, TOOLS } from '../../engine/tools.js';
import { mergedEntryCount } from '../../data/merge.js';
import { buildIconHTML, colorSeed, getMergedIcon } from '../icons.js';
import { buildToolCardHTML } from '../tool-stack.js';
import { buildScoreOptions } from '../entries-table.js';
import { queryHTML } from '../query-editor.js';
import { createDialog, showDialog } from './dialog.js';

function mergeDiagram() {
  const byPop = [...WORDLIST_PUBLISHERS].sort((a, b) => a.popularity - b.popularity);
  const sourceIcons = byPop.map(p => buildIconHTML(p.icon, p.name, colorSeed(p))).join('');
  return `<div class="faq-diagram faq-diagram--merge">
      <div class="faq-merge-sources">${sourceIcons}</div>
      <svg class="faq-merge-arrow" width="16" height="10" viewBox="0 0 16 10" aria-hidden="true"><path d="M2 2l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <div class="faq-merge-all">
        <div class="faq-merge-all-head">${getMergedIcon()}<span class="faq-merge-all-name">${MERGED_NAME}</span></div>
        <span class="faq-merge-count">${pluralize(mergedEntryCount(), 'entry', 'entries')}</span>
      </div>
    </div>`;
}

function syncDiagram() {
  return `<div class="faq-diagram faq-diagram--sync">
      <svg class="faq-sync-icon" aria-hidden="true"><use href="#${getBrowser().icon}"/></svg>
      <span class="faq-sync-arrow">⇄</span>
      <span class="faq-sync-emoji">📄</span>
      <span class="faq-sync-arrow">⇄</span>
      <svg class="faq-sync-icon" aria-hidden="true"><use href="#icon-crossword"/></svg>
    </div>`;
}

function toolsDiagram() {
  const cards = FEATURED_TOOLS.map(k => TOOLS[k] ? buildToolCardHTML(k, TOOLS[k], { allButton: false }) : '').join('');
  return `<div class="faq-diagram faq-diagram--tools" inert><div class="faq-tools-strip">${cards}</div></div>`;
}

function screenshot({ file, alt, caption, width, height }) {
  return `<figure class="faq-shot">
      <img src="img/${file}" alt="${esc(alt)}" width="${width}" height="${height}" loading="lazy">
      <figcaption>${caption}</figcaption>
    </figure>`;
}

function wordlistCreditsHTML() {
  return [...WORDLIST_PUBLISHERS]
    // John's own list — Grawlix's author, not a third party.
    .filter(p => p.id !== 'jkugelman')
    .sort((a, b) => a.popularity - b.popularity)
    .map(p => {
      const icon = buildIconHTML(p.icon, p.name, colorSeed(p));
      const name = p.homepage
        ? `<a href="${p.homepage}" target="_blank" rel="noopener">${esc(p.name)}</a>`
        : esc(p.name);
      const byline = p.author ? `<span class="acks-credit-by">by ${esc(p.author)}</span>` : '';
      return `<li class="acks-credit">${icon}<div class="acks-credit-text"><span>${name}</span>${byline}</div></li>`;
    })
    .join('');
}

const RULE_EXAMPLES = [
  ['Moving scores around', [
    ['score: 50', '60', 'bumps every 50 up to 60.'],
    ['score: 50+', '60+', 'lifts everything 50 and up by 10.'],
    ['score: 30-49', '20-39', 'slides that whole band down.'],
  ]],
  ['Picking out kinds of entries', [
    ['is: proper and words: 2+ and score: 50+', '60', 'bumps well-known full names like ROGER EBERT.'],
    ['is: proper and words: 1 and score: 40', '30', 'cools off lesser-known single names like CADMUS.'],
    ['is: initialism and length: 3-4', '30', 'catches short initialisms like DNC and HGTV.'],
    ['is: roman numeral', '20', 'buries numerals on their own, like XVIII.'],
    ['is: proper and ends with: roman numeral', '30', 'goes after names like PIUS VII.'],
    ['starts with: proper and is not: proper', '40', 'finds a name plus a common word, like ZOOM MEETING.'],
    [`ends with: "'s"`, '30', `handles possessives like EULER'S.`],
    ['ends with: " in", " at", " of", " by" and words: 2', '50', 'picks up phrasal verbs like CHAFE AT.'],
    ['contains: digits, symbols', '0', 'sinks anything like 2ND or 50% OFF.'],
  ]],
  ['Size and shape', [
    ['length: 15 and score: 50+', '60', 'rewards anything that spans a 15×15.'],
    ['length: 3 and score: 40-49', '30', 'pushes down three-letter crosswordese.'],
    ['pattern: "*ing" and words: 1 and length: 9+', '40', 'tames long -ING words, which are easy to coin.'],
    ['pattern: "#@#@#"', '50', 'picks out five-letter words that alternate consonants and vowels.'],
    ['regex: "^[rstlne]+$" and length: 8+', '40', 'marks down long entries spelled only with the Wheel of Fortune letters, like SEERESSES.'],
  ]],
];

const ruleExamplesHTML = () => RULE_EXAMPLES.map(([title, rules]) => `
  <p>${title}:</p>
  <ul>${rules.map(([q, out, desc]) => `
    <li><code>${queryHTML(q)}</code> → <code>${out}</code> ${esc(desc)}</li>`).join('')}
  </ul>`).join('');

// Each item's `slug` is a public URL (#/help/<slug>), so renaming one breaks
// links already shared.
const SECTIONS = [
  {
    title: 'What even is this?',
    items: [
      {
        slug: 'what-is-grawlix',
        q: 'Is this a wordlist manager, a search tool, or what?',
        a: `
          <p>Both, really. It's two things sharing one screen.</p>
          <p>The first is a wordlist <em>manager</em>. You probably already use a wordlist or three (XWI, Spread the Wordlist, Broda, your own), each scored on its own private scale. Grawlix rescales them onto one common scale and merges them into a single deduped list, which you download (or sync) and feed to your construction software. That's the bread and butter.</p>
          <p>The second is a word-finding <em>playground</em>. That same merged list is right there to search and slice, and you can stack tools on it (anagrams, rhymes, beheadments, rebus forms, a couple dozen more) to mine for theme ideas, or just shake loose the one entry that fits a stubborn corner.</p>`,
      },
      {
        slug: 'my-data',
        q: 'Do I need an account? Where does my stuff live?',
        a: `
          <p>No account, no login, nothing to sign into, and no Grawlix server holding your data either. Your wordlists, your edits, and your settings live entirely in your browser, on this device.</p>
          <p>That's the privacy upside: I can't see your lists, because they never leave your machine. It's also the catch worth knowing. Your data is tied to this one browser on this one computer, so if you clear your browser storage, it's gone. Keep a copy: every list has a <strong>Download</strong> button, and if you want something more hands-off, disk sync keeps a live file on your drive (and, through a cloud drive, on your other computers too).</p>`,
      },
    ],
  },
  {
    title: 'Rescoring & merging',
    items: [
      {
        slug: 'rescoring',
        q: `What's "rescoring," and why should I care?`,
        a: `
          <p>Different wordlists score on different scales. XWI is usable from 25-60 but STWL is dangerous below 50. Broda and Nediger run top out at 100. Mashing the lists together and sorting by score just gives you nonsense.</p>
          <p>Rescoring fixes that. For each list you write a few rules that translate its scores onto one shared scale, so a 50 is a 50 no matter which list it came from.</p>
          <p>The good news is you don't have to fiddle with any of this if you don't want to. Grawlix ships with opinionated defaults for the four big wordlists, so out of the box they're already aligned. You only crack open the rules when you import your own list, or you decide one of my defaults got something wrong. (And if you don't care about scores at all, you can skip rescoring entirely. See <em>Do I have to rescore anything?</em>)</p>
          <p>To change the rules, open <strong>Manage wordlists</strong> and click the list.</p>
          <p>Each rule names the entries it changes, like <code>score: 50 and is: proper</code>, and the score they get. You don't need to learn the syntax: click a rule and Grawlix suggests what to type, and every rule shows how many entries it catches as you go. For a tour, see <a href="#/help/rescore-rules">What can a rescore rule do?</a></p>`,
      },
      {
        slug: 'rescore-rules',
        q: 'What can a rescore rule do?',
        a: () => `
          <p>A rule is a condition and a new score. Every entry the condition catches gets that score. Rules run top to bottom, and each entry goes to the first rule that catches it. Some ideas to steal.</p>
          ${ruleExamplesHTML()}
          <p>Leave the new score blank and the entry keeps its score. Put a rule like that near the top and it shields those entries from the rules below it.</p>
          <p>Don't worry about memorizing any of this. Click a condition and Grawlix suggests what comes next, with a count beside each choice, and it says so if something doesn't make sense. The full list of fields is in the <strong>Cheat sheet</strong> below the rules.</p>`,
      },
      {
        slug: 'score-tiers',
        q: 'What do the score colors and tiers mean?',
        a: `
          <p>Scores show up as colored badges, and the color is the tier. Out of the box: <strong>great</strong> (60+), <strong>good</strong> (50+), <strong>fair</strong> (40+), <strong>meh</strong> (30+), <strong>bad</strong> (below 30). Hover any badge, whether in the table, the entry panel, or the tier picker, and it names the tier.</p>
          <p>Don't like the cutoffs or the labels? They're yours. Open <strong>Manage wordlists</strong> and click All Wordlists to rename a tier or move a line. It's optional decoration, though. Unlabeled scores still display fine, you just won't get a name on hover.</p>`,
      },
      {
        slug: 'rescore-entries',
        q: 'How do I rescore entries?',
        a: () => {
          const maxDigit = Math.max(0, ...buildScoreOptions().map(o => o.hint).filter(h => h != null));
          const shortcuts = maxDigit > 0 ? `<kbd>Alt</kbd>+<kbd>0</kbd> through <kbd>Alt</kbd>+<kbd>${maxDigit}</kbd>` : 'Alt+<kbd>0</kbd>';
          return `
          <p>Click an entry's <em>score</em> to pop up a score picker and change it to one of the predefined tiers. Want to save a click? Use ${shortcuts} to do it in one key press. You needn't open the picker at all, just hover a score badge and press one of the shortcuts. Point, press, on to the next one.</p>
          <p>Want a score that isn't one of your tiers, or to fix a comment or rename the entry? Click the entry's <em>text</em> instead for the full editor.</p>`;
        },
      },
      {
        slug: 'all-wordlists',
        q: `What is "All Wordlists"?`,
        a: () => `
          <p>It's the headline view: every wordlist you've enabled, rescored onto the common scale and merged into one deduped list. It's what you're looking at by default, and it's what you download or sync and hand to your construction software.</p>
          ${mergeDiagram()}
          <p>When the same word shows up in several lists, the highest-priority list that has it wins. Priority is just the order your lists sit in, which you set under <strong>Manage wordlists</strong>. You can switch that to <strong>Highest score wins</strong> or <strong>Lowest score wins</strong> there, which only makes sense once all your lists are rescored onto the same scale. Either way, your edits always win. Search, the tools, the histogram, all of it runs against this merged view unless you deliberately switch to a single list.</p>`,
      },
      {
        slug: 'sources-column',
        q: `What's the Sources column?`,
        a: `
          <p>Over on the right of the table, each row shows little icons for the wordlists that have that entry: one slot per list, with a gap where a list doesn't have it. A single icon means the entry is unique to that list; several mean it's shared.</p>
          <p>The lists in full color are the ones whose spelling, score, or comment you're actually seeing; any dimmed ones have the entry too, but another list won. Click the entry for the full rundown.</p>`,
      },
      {
        slug: 'my-edits',
        q: `What is "My Edits"?`,
        a: `
          <p>My Edits is your personal layer, created for you automatically the first time you open Grawlix. Any time you change a score, fix a comment, rename an entry, or add a word, it lands here, never in the original wordlist.</p>
          <p>That's deliberate. The source lists stay pristine. They update from their authors, and you don't want your edits clobbered when they do.</p>`,
      },
    ],
  },
  {
    title: 'Disk sync',
    items: [
      {
        slug: 'disk-sync',
        q: 'What is disk sync, and why do I want it?',
        a: () => `
          <p>Grawlix keeps everything in your browser. Disk sync adds a bridge: it ties one of your lists to one file on your hard drive (ideally the exact file your construction software already loads) and keeps the two matched automatically.</p>
          ${syncDiagram()}
          <p>Why bother? Without it, every time you rescore a word you'd re-download the merged list and re-import it into your grid software by hand. With sync, you edit in Grawlix and the file just updates underneath. Two flavors, depending on the list:</p>
          <ul>
            <li><strong>All Wordlists and individual sources sync one way.</strong> Grawlix writes the file, your software reads it. Point your software at All Wordlists for the unified list.</li>
            <li><strong>My Edits syncs both ways.</strong> Edit it in Grawlix or in your software and the two reconcile. It's the file your software both reads and writes.</li>
          </ul>
          <p>Heads up: disk sync needs a Chromium desktop browser. See <em>Why don't I see a sync button?</em></p>`,
      },
      {
        slug: 'sync-setup',
        q: 'How do I set it up?',
        a: `
          <p>Switch to the list you want, then click on <strong>Sync to disk</strong>. Pick an existing file on your computer, or create a new one. That's it.</p>
          <p>Changes you make will be written to that file automatically. If your construction software reads from there, changes you make in Grawlix will show up when gridding. It's that easy.</p>
          <p>For the other half of the job, see <a href="#/help/ingrid">How do I sync wordlists with Ingrid?</a></p>`
      },
      {
        slug: 'ingrid',
        q: 'How do I sync wordlists with Ingrid?',
        a: () => `
          <p>Two files is the setup I recommend: <strong>All Wordlists</strong> to fill from and <strong>My Edits</strong> as the one Ingrid writes back to.</p>
          <p><strong>First, in Grawlix.</strong></p>
          <ol>
            <li>Pick <strong>All Wordlists</strong> and click <strong>Sync to disk</strong>. Save it somewhere you'll find again.</li>
            <li>Pick <strong>My Edits</strong> and sync that to a second file.</li>
          </ol>
          <p><strong>Then, in Ingrid.</strong> Open <strong>Preferences</strong> and pick <strong>Word Lists</strong>.</p>
          <ol start="3">
            <li>Click <strong>Add</strong> and choose the file you synced All Wordlists to. Click <strong>Add</strong> again and choose your My Edits file.</li>
            <li>Use <strong>Move Up</strong> to put My Edits on top. Ingrid lets higher lists override lower ones, and you want your own edits winning.</li>
            <li>Untick Ingrid's bundled <strong>Spread the Wordlist</strong>. You aren't losing it: as long as it's enabled in Grawlix it's already inside All Wordlists, rescored onto your scale.</li>
          </ol>
          ${screenshot({
            file: 'ingrid-word-lists.png',
            alt: `Ingrid's Preferences dialog on the Word Lists pane, with My Edits and All Wordlists ticked and Spread the Wordlist unticked`,
            caption: 'Both synced files loaded, My Edits on top.',
            width: 626,
            height: 681,
          })}
          <ol start="6">
            <li>Select <strong>My Edits</strong>, click <strong>Details...</strong>, and tick <strong>Use as Personal List</strong>. It picks up a ✏️ in the list. That's what makes Ingrid write back into the file when you score or add a word mid-fill, which is how your changes find their way home to Grawlix.</li>
            <li>While you're in there, choose <strong>Remove diacritics</strong> and <strong>Skip punctuation</strong>. Grawlix writes entries out in full, accents and hyphens and all, and these two let Ingrid fit them into a grid. RÉSUMÉ fills as RESUME, CO-OP fills as COOP.</li>
          </ol>
          ${screenshot({
            file: 'ingrid-list-details.png',
            alt: `Ingrid's per-list details dialog showing Use as Personal List ticked, Remove diacritics selected, and Skip punctuation selected`,
            caption: 'The three settings that matter, on My Edits.',
            width: 466,
            height: 663,
          })}
          <p>That's the whole setup. Rescore something in Grawlix and Ingrid's fill list has it. Score something in Ingrid while you're filling and it turns up in My Edits back in Grawlix, usually before you've finished the corner.</p>
          <p>Crossfire and Crossword Compiler load the same two files perfectly well. You just have to refresh them by hand to pick up changes, which is why I reach for Ingrid.</p>`,
      },
      {
        slug: 'two-computers',
        q: 'Can I use the same wordlist across two computers?',
        a: `
          <p>Yes, with a little help from a cloud drive. Grawlix itself ships zero cloud code; your data is per-browser. But disk sync points at a <em>file</em>, and if that file lives in Dropbox, iCloud Drive, OneDrive, or Google Drive, your cloud client shuttles it between machines for you. On each computer, sync the same list to that same file.</p>`,
      },
      {
        slug: 'no-sync-button',
        q: `Why don't I see a sync button, or why is it grayed out?`,
        a: `
          <p>Disk sync rides on a browser feature called the File System Access API, and today only Chromium desktop browsers have it: Chrome, Edge, Brave, Arc, and the like. So where you are decides what you get:</p>
          <ul>
            <li>On a phone or tablet, there's no sync button at all. Keeping a file in sync just isn't something a mobile browser can do, so I don't show a control that can't work.</li>
            <li>In Firefox or Safari on the desktop, the <strong>Sync to disk</strong> button is there, but clicking it just explains that sync needs a Chromium browser. Sorry, my hands are tied.</li>
          </ul>
          <p>Either way, you're not stuck. <strong>Download</strong> works everywhere and hands you the same file to load into your software, you just do it by hand instead of automatically.</p>`,
      },
    ],
  },
  {
    title: 'Tools & chaining',
    items: [
      {
        slug: 'all-mode',
        q: `What's the ✱ "all-mode" button for?`,
        a: `
          <p>A lot of tools take an input string. Anagram takes a word to rearrange, Letter bank takes a set of letters, Rhymes takes a word to rhyme with. Normally you hand it one input and get back that input's results.</p>
          <p>The <strong>✱</strong> button runs the tool over every possible input at once and shows all the results together. Instead of the anagrams of one word, you get every set of anagrams in your list; instead of the rhymes for one word, every rhyme family. Each group comes back as its own row, with a count and its members.</p>
          <p>Tools that can do this wear a small ✱ in the corner of their gallery card. Click the ✱ on the tool's input in your stack to toggle it on. Only one tool can be in all-mode at a time.</p>`,
      },
      {
        slug: 'stacking-tools',
        q: `What's the deal with stacking tools?`,
        a: () => `
          <p>Tools chain. The gallery sits at the top of the screen; click a tool's card and it drops onto a <strong>stack</strong>. Click another and it stacks below. The stack runs top to bottom like a pipeline: the first tool reads whatever you're looking at (usually All Wordlists), and each tool after it works on the output of the one above. Search is always pinned as the last step.</p>
          ${toolsDiagram()}
          <p>So you can type a set of letters into <strong>Anagram</strong>, then type a few known crossing letters in the search bar to narrow the anagrams down, live, to the ones that actually fit. The search filters Anagram's output as you type. Drag a row by its handle to reorder the pipeline, or hit the ✕ to drop a tool. Stacking is what turns a pile of one-trick tools into combinations.</p>
          <p>What's that good for? Heck if I know. Try it, let me know what you come up with.</p>`,
      },
      {
        slug: 'umiaq',
        q: 'What are Umiaq and those capital-letter patterns?',
        a: `
          <p>Umiaq is search with <strong>variables</strong>. A capital letter stands for a chunk of letters that has to come out the same everywhere it appears. So <code>ABBA</code> finds words whose halves mirror, like NOON and DEED, and <code>AA</code> finds doubled words like MAMA and TUTU.</p>
          <p>The real trick is reusing a variable across several words, separated by a semicolon. <code>AB;BA</code> finds pairs where the same two chunks swap places, like APE and PEA, and the matches come back as side-by-side tuples. That cross-word link is the thing plain search and regex cannot express.</p>
          <p>Everything else works like the search bar: lowercase is literal, <code>?</code> is any letter or digit, <code>*</code> any run, <code>#</code> and <code>@</code> a consonant and a vowel. The one quirk is that capitals are variables here, so unlike the search bar, case matters.</p>
          <p>A space in the query is a literal space, so <code>A B;B A</code> finds two-word swaps like PEANUT BUTTER and BUTTER PEANUT. Put a backslash before a punctuation mark to match it as written, like <code>u\\.s\\.</code>, or before a capital to match that capital exactly.</p>`,
      },
    ],
  },
  {
    title: 'Other',
    items: [
      {
        slug: 'credits',
        q: 'Who can I thank for this?',
        a: () => `
          <p>Grawlix is made with ♥ by John Kugelman. Got feedback? <a href="mailto:john@kugelman.name?subject=Grawlix">E-mail me</a>.</p>
          <p>It would be useless without other people's wordlists. Thank you to the constructors who make and share them:</p>
          <ul class="acks-list">${wordlistCreditsHTML()}</ul>
          <p>The tools are heavily inspired by <a href="https://aaronson.org/wordlisted/" target="_blank" rel="noopener">Wordlisted</a> by Adam Aaronson. Kudos to Adam for putting wordlist searching in everyone's hands.</p>
          <p>The Umiaq tool's variable-and-pattern language comes from <a href="https://www.quinapalus.com/qat.html" target="_blank" rel="noopener">Qat</a> by Mark Owen, and its matcher is a reimplementation of <a href="https://github.com/crosswordnexus/umiaq" target="_blank" rel="noopener">Umiaq</a> by Alex Boisvert (Crossword Nexus), used under the MIT license.</p>`,
      },
    ],
  },
];

export const HelpDialog = (() => {
  let el, body;

  function mount() {
    ({ el, body } = createDialog('help-dialog', { labelledby: 'help-title' }));
    body.addEventListener('toggle', onToggle, true);   // toggle doesn't bubble
  }

  function sectionHTML(section) {
    const items = section.items.map(it => {
      const answer = typeof it.a === 'function' ? it.a() : it.a;
      return `<details class="faq-item" id="faq-${it.slug}" name="faq"><summary>${it.q}</summary><div class="faq-answer">${answer}</div></details>`;
    }).join('');
    return `<section class="faq-section"><h3>${section.title}</h3>${items}</section>`;
  }

  function render() {
    body.innerHTML = `
      <button type="button" class="dialog-close-btn" aria-label="Close">✕</button>
      <h2 id="help-title">Help</h2>
      ${SECTIONS.map(sectionHTML).join('')}
      <div class="dialog-footer">
        <button type="button" class="primary dialog-cancel-btn">Done</button>
      </div>`;
  }

  function reveal(slug) {
    if (!slug) return;
    const item = body.querySelector(`#faq-${CSS.escape(slug)}`);
    if (!item) return;
    item.open = true;
    item.scrollIntoView({ block: 'start' });
  }

  // The dialog mirrors the #/help hash so it's deep-linkable: closing strips the
  // hash, and the boot/hashchange sync (in actions) drives open/close from it.
  function clearHash() {
    if (location.hash.startsWith('#/help')) history.replaceState(null, '', location.pathname + location.search);
  }

  function onToggle(e) {
    const item = e.target;
    if (!el.open || !item.matches('.faq-item')) return;
    const openItem = body.querySelector('.faq-item[open]');
    history.replaceState(history.state, '', openItem ? `#/help/${openItem.id.slice('faq-'.length)}` : '#/help');
    if (item.open && item.getBoundingClientRect().top < el.getBoundingClientRect().top) item.scrollIntoView({ block: 'start' });
  }

  function open(slug) {
    if (el.open) { reveal(slug); return; }
    render();
    showDialog(el, clearHash);
    reveal(slug);
  }

  return {
    mount,
    open,
    close: () => el.close(),
    isOpen: () => el.open,
  };
})();
